import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import {
  API_GET_JOB_RECORD,
  API_GET_UPLOAD_URL,
  API_UPLOAD_JOB_DOCUMENTS,
  PROJECT_API_KEY
} from 'src/@core/services/util/api.endpoints';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'hour4u-admin-public-document-upload',
  templateUrl: './public-document-upload.component.html',
  styleUrls: ['./public-document-upload.component.scss']
})
export class PublicDocumentUploadComponent implements OnInit {
  form: FormGroup;
  fields: Array<{
    question: string;
    controlName: string;
    type: 'text' | 'tel';
    placeholder?: string;
    required?: boolean;
    hint?: string;
    maxLength?: number;
    inputMode?: string;
    autocomplete?: string;
    isPhone?: boolean;
    isPan?: boolean;
  }> = [];
  selectedFiles: { [key: string]: File | null } = {
    pan_card: null,
    address_proof: null,
    pay_slip: null,
    cibil_report: null
  };
  isSubmitted = false;
  calluid: string = null;
  /** From getJobRecord; used for get-upload-url and uploadDocuments */
  jobId: string = null;
  projectId: string = null;
  entityId: string = null;
  latestQuestionsAsked: Array<{ question: string; answer: string }> = [];
  isLoadingJobRecord = false;
  loadError: string = null;
  submitError: string = null;
  isSubmitting = false;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.form = this.fb.group({});
    this.buildInitialFields();

    this.route.paramMap.subscribe(params => {
      const uid = params.get('calluid');
      this.calluid = uid;
      this.jobId = uid;
      if (uid) {
        this.fetchJobRecord(uid);
      }
    });
  }

  onFileChange(event: Event, field: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles[field] = input.files[0];
    }
  }

  onPanInput(): void {
    const panField = this.fields.find(f => f.isPan);
    const panControl = panField ? this.form.get(panField.controlName) : null;
    const panNumber = panControl ? panControl.value || '' : '';
    if (panField) {
      this.form.patchValue({ [panField.controlName]: String(panNumber).toUpperCase() }, { emitEvent: false });
    }
  }

  submit(): void {
    void this.runSubmit();
  }

  private buildInitialFields(): void {
    // Base fields (these existed earlier). Everything else is appended from questions_asked.
    this.fields = [
      {
        question: 'Full Name',
        controlName: 'full_name',
        type: 'text',
        placeholder: 'John Doe',
        required: true,
        autocomplete: 'name',
        inputMode: 'text'
      },
      {
        question: 'Phone Number',
        controlName: 'phone_number',
        type: 'tel',
        placeholder: '00000 00000',
        required: true,
        autocomplete: 'tel',
        inputMode: 'numeric',
        maxLength: 10,
        isPhone: true
      },
      {
        question: 'PAN Number',
        controlName: 'pan_number',
        type: 'text',
        placeholder: 'ABCDE1234F',
        required: true,
        autocomplete: 'off',
        inputMode: 'text',
        maxLength: 10,
        hint: 'Format: 5 letters, 4 digits, 1 letter',
        isPan: true
      }
    ];

    this.rebuildFormControls();
  }

  private rebuildFormControls(): void {
    // Add missing controls (do not drop existing values)
    this.fields.forEach(f => {
      if (this.form.get(f.controlName)) {
        return;
      }
      const validators = [];
      if (f.required) {
        validators.push(Validators.required);
      }
      if (f.controlName === 'full_name') {
        validators.push(Validators.minLength(2));
      }
      if (f.isPhone) {
        validators.push(Validators.pattern('^[6-9][0-9]{9}$'));
      }
      if (f.isPan) {
        validators.push(Validators.pattern('^[A-Z]{5}[0-9]{4}[A-Z]{1}$'));
      }
      this.form.addControl(f.controlName, new FormControl(null, validators));
    });
  }

  private normalizeControlName(question: string): string {
    const raw = (question || '').trim().toLowerCase();
    const safe = raw.replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    return safe || 'field';
  }

  private normalizeAnswer(answer: any): string {
    const v = answer == null ? '' : String(answer).trim();
    if (!v) return '';
    const lower = v.toLowerCase();
    if (lower === 'not asked' || lower === 'not answered' || lower === 'na' || lower === 'n/a') {
      return '';
    }
    return v;
  }

  private upsertDynamicQuestions(questionsAsked: any[]): void {
    if (!Array.isArray(questionsAsked) || questionsAsked.length === 0) {
      return;
    }

    const existingByQuestion = new Map(this.fields.map(f => [f.question.toLowerCase(), f]));

    questionsAsked.forEach(q => {
      const question = q && q.question ? String(q.question).trim() : '';
      if (!question) return;

      // Keep our base fields for these questions (so validators/types stay consistent)
      const key = question.toLowerCase();
      const already = existingByQuestion.get(key);
      if (already) return;

      const controlName = this.normalizeControlName(question);
      // Avoid collisions
      const uniqueControlName = this.form.get(controlName)
        ? `${controlName}_${Math.random().toString(16).slice(2, 6)}`
        : controlName;

      this.fields.push({
        question,
        controlName: uniqueControlName,
        type: 'text',
        placeholder: '',
        required: false,
        inputMode: 'text'
      });
    });

    this.rebuildFormControls();
  }

  private patchAnswersFromQuestionsAsked(questionsAsked: any[]): void {
    if (!Array.isArray(questionsAsked) || questionsAsked.length === 0) {
      return;
    }

    const patches: Record<string, any> = {};
    const fieldByQuestion = new Map(this.fields.map(f => [f.question.toLowerCase(), f]));

    questionsAsked.forEach(q => {
      const question = q && q.question ? String(q.question).trim() : '';
      if (!question) return;
      const f = fieldByQuestion.get(question.toLowerCase());
      if (!f) return;

      const ans = this.normalizeAnswer(q.answer);
      if (ans) {
        patches[f.controlName] = ans;
      }
    });

    if (Object.keys(patches).length) {
      this.form.patchValue(patches);
      this.onPanInput();
    }
  }

  private buildQuestionsAskedForSubmit(): Array<{ question: string; answer: string }> {
    const fieldByQuestion = new Map(this.fields.map(f => [f.question.toLowerCase(), f]));

    // If API provided the canonical list, preserve its order + include "Not Asked" if that's what it had
    if (this.latestQuestionsAsked && this.latestQuestionsAsked.length) {
      return this.latestQuestionsAsked.map(item => {
        const q = item && item.question ? String(item.question).trim() : '';
        const existing = item && item.answer != null ? String(item.answer) : '';
        const f = q ? fieldByQuestion.get(q.toLowerCase()) : null;

        const ctrl = f ? this.form.get(f.controlName) : null;
        const v = ctrl ? String(ctrl.value || '').trim() : '';

        // If user entered something, use it; else keep server-provided placeholder like "Not Asked"
        return {
          question: q,
          answer: v ? v : existing
        };
      });
    }

    // Fallback: derive from current dynamic fields (no "Not Asked" placeholders)
    return this.fields.map(f => {
      const ctrl = this.form.get(f.controlName);
      const v = ctrl ? String(ctrl.value || '').trim() : '';
      return { question: f.question, answer: v || 'Not Answered' };
    });
  }

  private ensureJobRecordReady(calluid: string): Promise<boolean> {
    if (this.projectId && this.entityId) {
      return Promise.resolve(true);
    }
    if (!calluid) {
      return Promise.resolve(false);
    }
    this.isLoadingJobRecord = true;
    this.loadError = null;

    const headers = new HttpHeaders({ 'x-api-key': PROJECT_API_KEY });
    const params = new HttpParams().set('job_id', calluid);

    return new Promise(resolve => {
      this.http.get(API_GET_JOB_RECORD, { headers: headers, params: params }).subscribe(
        (response: any) => {
          if (response) {
            const phoneField = this.fields.find(f => f.isPhone);
            const existingPhoneCtrl = phoneField ? this.form.get(phoneField.controlName) : null;
            const existingPhoneVal = existingPhoneCtrl ? existingPhoneCtrl.value : null;
            const rawPhone = response.client_number || existingPhoneVal;
            const digits = rawPhone ? String(rawPhone).replace(/\D/g, '') : '';
            const phone10 = digits.length > 10 ? digits.slice(-10) : digits;

            this.jobId = response.job_id || response.jobId || this.calluid;
            this.projectId = response.project_id || response.projectId;
            this.entityId = response.entity_id || response.entityId;
            this.latestQuestionsAsked = Array.isArray(response.questions_asked) ? response.questions_asked : [];

            this.upsertDynamicQuestions(response.questions_asked);
            this.patchAnswersFromQuestionsAsked(response.questions_asked);

            const patches: Record<string, any> = {};
            const fullNameField = this.fields.find(f => f.controlName === 'full_name');
            if (fullNameField) {
              const existingNameCtrl = this.form.get(fullNameField.controlName);
              const existingNameVal = existingNameCtrl ? existingNameCtrl.value : null;
              patches[fullNameField.controlName] = response.client_name || existingNameVal;
            }
            if (phoneField) {
              patches[phoneField.controlName] = phone10 || rawPhone;
            }
            if (Object.keys(patches).length) {
              this.form.patchValue(patches);
            }
          }
          this.isLoadingJobRecord = false;
          resolve(!!(this.projectId && this.entityId));
        },
        () => {
          this.loadError = 'Could not fetch details for this link. Please fill the form manually.';
          this.projectId = null;
          this.entityId = null;
          this.isLoadingJobRecord = false;
          resolve(false);
        }
      );
    });
  }

  private async runSubmit(): Promise<void> {
    this.submitError = null;
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.selectedFiles.pan_card) {
      return;
    }

    if (!this.calluid) {
      this.submitError = 'Invalid link. Missing job reference id.';
      return;
    }

    const ready = await this.ensureJobRecordReady(this.calluid);
    if (!ready) {
      this.submitError =
        'We could not verify your job record for upload. Please refresh the page or try again later.';
      return;
    }

    this.isSubmitting = true;

    try {
      const pan_card = await this.uploadOneFile(this.selectedFiles.pan_card);
      const address_proof = await this.uploadOneFile(this.selectedFiles.address_proof);
      const pay_slip = await this.uploadOneFile(this.selectedFiles.pay_slip);
      const cibil_report = await this.uploadOneFile(this.selectedFiles.cibil_report);

      if (!pan_card) {
        this.submitError = 'PAN document upload did not return a file URL. Please try again.';
        return;
      }

      const payload: any = {
        job_id: this.jobId || this.calluid,
        pan_card: pan_card,
        address_proof: address_proof || '',
        pay_slip: pay_slip || '',
        cibil_report: cibil_report || '',
        questions_asked: this.buildQuestionsAskedForSubmit()
      };

      const headers = new HttpHeaders({
        'x-api-key': PROJECT_API_KEY,
        'Content-Type': 'application/json'
      });

      await this.http.post(API_UPLOAD_JOB_DOCUMENTS, payload, { headers: headers }).toPromise();

      this.isSubmitted = true;
    } catch (e) {
      this.submitError = 'File upload or submit failed. Please try again.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async uploadOneFile(file: File | null): Promise<string> {
    if (!file) {
      return '';
    }

    const uploadUrlResponse = await this.getUploadUrl(file);
    if (!uploadUrlResponse.uploadUrl || !uploadUrlResponse.s3_key) {
      throw new Error('Failed to get upload URL');
    }

    await this.uploadToS3(file, uploadUrlResponse.uploadUrl, uploadUrlResponse.headers || {});

    return this.toDocumentUrl(uploadUrlResponse, uploadUrlResponse.uploadUrl);
  }

  private getUploadUrl(file: File): Promise<{
    uploadUrl: string;
    s3_key: string;
    bucket?: string;
    method?: string;
    headers?: { [key: string]: string };
    expiresIn?: number;
    file_url?: string;
    publicUrl?: string;
    url?: string;
    imageUrl?: string;
  }> {
    return new Promise((resolve, reject) => {
      const headers = new HttpHeaders({
        'x-api-key': PROJECT_API_KEY,
        'Content-Type': 'application/json'
      });

      const payload = {
        project_id: this.projectId,
        entity_id: this.entityId,
        filename: file.name,
        contentType: file.type || 'application/octet-stream'
      };

      this.http.post(API_GET_UPLOAD_URL, payload, { headers: headers }).subscribe(
        (response: any) => {
          resolve({
            uploadUrl: response.uploadUrl,
            s3_key: response.s3_key,
            bucket: response.bucket,
            method: response.method,
            headers: response.headers || {},
            expiresIn: response.expiresIn,
            file_url: response.file_url,
            publicUrl: response.publicUrl,
            url: response.url,
            imageUrl: response.imageUrl
          });
        },
        err => reject(err)
      );
    });
  }

  private uploadToS3(
    file: File,
    uploadUrl: string,
    extraHeaders: { [key: string]: string }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve();
        } else {
          reject(new Error('S3 upload failed: ' + xhr.status));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('S3 upload network error')));
      xhr.addEventListener('abort', () => reject(new Error('S3 upload aborted')));

      xhr.open('PUT', uploadUrl);
      Object.keys(extraHeaders).forEach(key => {
        xhr.setRequestHeader(key, extraHeaders[key]);
      });
      xhr.send(file);
    });
  }

  /**
   * Public object URL matching the bucket/key used for the presigned PUT.
   * API fields like file_url/imageUrl often point at image-data-hour4u-2 even when
   * the presigned URL targets another bucket (e.g. teleagent leads files).
   */
  private publicUrlFromPresignedPut(presignedPutUrl: string): string {
    if (!presignedPutUrl || presignedPutUrl.indexOf('http') !== 0) {
      return '';
    }
    try {
      const u = new URL(presignedPutUrl);
      if (!u.hostname || u.pathname.length <= 1) {
        return '';
      }
      return `${u.origin}${u.pathname}`;
    } catch {
      return '';
    }
  }

  private toDocumentUrl(
    response: {
      file_url?: string;
      publicUrl?: string;
      url?: string;
      imageUrl?: string;
      s3_key?: string;
    },
    presignedPutUrl?: string
  ): string {
    if (!response) {
      return '';
    }
    const fromPut = presignedPutUrl ? this.publicUrlFromPresignedPut(presignedPutUrl) : '';
    if (fromPut) {
      return fromPut;
    }
    if (response.file_url) {
      return response.file_url;
    }
    if (response.publicUrl) {
      return response.publicUrl;
    }
    if (response.url && response.url.indexOf('http') === 0) {
      return response.url;
    }
    if (response.imageUrl) {
      return response.imageUrl;
    }
    if (response.s3_key) {
      if (response.s3_key.indexOf('http') === 0) {
        return response.s3_key;
      }
      return environment.image_url + response.s3_key;
    }
    return '';
  }

  private fetchJobRecord(calluid: string): void {
    this.isLoadingJobRecord = true;
    this.loadError = null;
    this.projectId = null;
    this.entityId = null;
    this.jobId = calluid;

    const headers = new HttpHeaders({ 'x-api-key': PROJECT_API_KEY });
    const params = new HttpParams().set('job_id', calluid);

    this.http.get(API_GET_JOB_RECORD, { headers: headers, params: params }).subscribe(
      (response: any) => {
        if (response) {
          const phoneField = this.fields.find(f => f.isPhone);
          const existingPhoneCtrl = phoneField ? this.form.get(phoneField.controlName) : null;
          const existingPhoneVal = existingPhoneCtrl ? existingPhoneCtrl.value : null;
          const rawPhone = response.client_number || existingPhoneVal;
          const digits = rawPhone ? String(rawPhone).replace(/\D/g, '') : '';
          const phone10 = digits.length > 10 ? digits.slice(-10) : digits;

          this.jobId = response.job_id || response.jobId || this.calluid;
          this.projectId = response.project_id || response.projectId;
          this.entityId = response.entity_id || response.entityId;
          this.latestQuestionsAsked = Array.isArray(response.questions_asked) ? response.questions_asked : [];

          this.upsertDynamicQuestions(response.questions_asked);
          this.patchAnswersFromQuestionsAsked(response.questions_asked);

          const patches: Record<string, any> = {};
          const fullNameField = this.fields.find(f => f.controlName === 'full_name');
          if (fullNameField) {
            const existingNameCtrl = this.form.get(fullNameField.controlName);
            const existingNameVal = existingNameCtrl ? existingNameCtrl.value : null;
            patches[fullNameField.controlName] = response.client_name || existingNameVal;
          }
          if (phoneField) {
            patches[phoneField.controlName] = phone10 || rawPhone;
          }
          if (Object.keys(patches).length) {
            this.form.patchValue(patches);
          }
        }
        this.isLoadingJobRecord = false;
      },
      () => {
        this.loadError = 'Could not fetch details for this link. Please fill the form manually.';
        this.projectId = null;
        this.entityId = null;
        this.isLoadingJobRecord = false;
      }
    );
  }
}
